import {
  Dialog,
  DialogContent,
  DialogTitle,
  Button,
  Stack,
  TextField,
  MenuItem,
  Box,
  IconButton,
} from "@mui/material";
import { Formik } from "formik";
import FreeSolo from "../../components/common/SoloAuto";
import * as Yup from "yup";
import CloseIcon from "@mui/icons-material/Close"; // Importing CloseIcon
const schema = Yup.object({
  title: Yup.string().required("Title required"),
  receiverName: Yup.string().required("Receiver required"),
  date: Yup.string().required("Date required"),

  // quantity optional
  quantity: Yup.number()
    .transform((v, orig) =>
      orig === "" || orig === null || orig === undefined
        ? undefined
        : Number(orig),
    )
    .min(0, "Quantity must be 0 or more")
    .nullable(),

  amount: Yup.number().min(0).required("Amount required"),
  paymentType: Yup.string()
    .oneOf(["full", "partial"])
    .required("Payment type required"),

  // payment method required (recommended)
  paymentMethod: Yup.string()
    .oneOf(["cash", "online", "cheque", "bank_transfer", "card", "other"])
    .required("Payment method required"),

  paidAmount: Yup.number().min(0),
  description: Yup.string().max(500, "Max 500 chars"),
});

export default function TransactionFormDialog({
  open,
  onClose,
  initial,
  onSubmit,
  titles,
  names,
}) {
  const isEdit = Boolean(initial?._id);

  return (
    <Dialog fullScreen={window.innerWidth < 430} open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ fontWeight: 900 }}>
        {isEdit ? "Edit Transaction" : "Add Transaction"}
      </DialogTitle>
      <IconButton
        onClick={onClose}
        sx={{
          position: "absolute",
          top: 8,
          right: 8,
          color: "gray",
        }}
      >
        <CloseIcon />
      </IconButton>
      <DialogContent>
        <Formik
          enableReinitialize
          initialValues={{
            title: initial?.title || "",
            receiverName: initial?.receiverName || "",
            date: initial?.date ? String(initial.date).slice(0, 10) : "",

            quantity: initial?.quantity ?? "",

            amount: initial?.amount ?? "",
            paymentType: initial?.paymentType || "full",

            paymentMethod: initial?.paymentMethod || "cash",

            paidAmount: initial?.paidAmount ?? "",
            description: initial?.description || "",
          }}
          validationSchema={schema}
          onSubmit={(values) => {
            const amt = Number(values.amount || 0);
            const paid =
              values.paymentType === "full"
                ? amt
                : Math.min(Number(values.paidAmount || 0), amt);

            onSubmit({
              title: values.title,
              receiverName: values.receiverName,
              date: values.date,

              // optional: send only if user entered
              quantity:
                values.quantity === "" ? undefined : Number(values.quantity),

              amount: amt,
              paymentType: values.paymentType,
              paymentMethod: values.paymentMethod,

              paidAmount: paid,
              description: values.description || "",
            });
          }}
        >
          {({
            values,
            handleChange,
            handleSubmit,
            touched,
            errors,
            isSubmitting,
            setFieldValue,
          }) => (
            <Box component="form" onSubmit={handleSubmit}>
              {/* Row 1 */}
              <FreeSolo
                fullWidth
                titles={titles}
                margin="normal"
                label="Name"
                name="receiverName"
                value={values.receiverName}
                onChange={handleChange}
                error={touched.receiverName && !!errors.receiverName}
                helperText={touched.receiverName && errors.receiverName}
                setFieldValue={setFieldValue}
              />

              <FreeSolo
                fullWidth
                titles={titles}
                margin="normal"
                label="Title"
                name="title"
                value={values.title}
                onChange={handleChange}
                error={touched.title && !!errors.title}
                helperText={touched.title && errors.title}
                setFieldValue={setFieldValue}
              />

              {/* <TextField
                fullWidth
                autoComplete="name"
                margin="normal"
                label="Receiver Name"
                name="receiverName"
                value={values.receiverName}
                onChange={handleChange}
                error={touched.receiverName && !!errors.receiverName}
                helperText={touched.receiverName && errors.receiverName}
              /> */}

              {/* Row 2 */}
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1.5}
                mt={1}
              >
                <TextField
                  fullWidth
                  margin="normal"
                  label="Date"
                  type="date"
                  name="date"
                  value={values.date}
                  onChange={handleChange}
                  InputLabelProps={{ shrink: true }}
                  error={touched.date && !!errors.date}
                  helperText={touched.date && errors.date}
                />

                <TextField
                  fullWidth
                  margin="normal"
                  label="Quantity (optional)"
                  name="quantity"
                  type="number"
                  value={values.quantity}
                  onChange={handleChange}
                  error={touched.quantity && !!errors.quantity}
                  helperText={touched.quantity && errors.quantity}
                />
              </Stack>

              {/* Row 3 */}
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1.5}
                mt={1}
              >
                <TextField
                  fullWidth
                  margin="normal"
                  label="Amount"
                  name="amount"
                  type="number"
                  value={values.amount}
                  onChange={(e) => {
                    handleChange(e);
                    if (values.paymentType === "full") {
                      setFieldValue("paidAmount", e.target.value);
                    }
                  }}
                  error={touched.amount && !!errors.amount}
                  helperText={touched.amount && errors.amount}
                />

                <TextField
                  fullWidth
                  margin="normal"
                  select
                  label="Payment Type"
                  name="paymentType"
                  value={values.paymentType}
                  onChange={(e) => {
                    handleChange(e);
                    const type = e.target.value;
                    if (type === "full")
                      setFieldValue("paidAmount", values.amount || 0);
                    if (
                      type === "partial" &&
                      (values.paidAmount === "" ||
                        values.paidAmount === undefined)
                    ) {
                      setFieldValue("paidAmount", 0);
                    }
                  }}
                >
                  <MenuItem value="full">Full</MenuItem>
                  <MenuItem value="partial">Partial</MenuItem>
                </TextField>
              </Stack>

              {/* Row 4 */}
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1.5}
                mt={1}
              >
                <TextField
                  fullWidth
                  margin="normal"
                  select
                  label="Payment Method"
                  name="paymentMethod"
                  value={values.paymentMethod}
                  onChange={handleChange}
                  error={touched.paymentMethod && !!errors.paymentMethod}
                  helperText={touched.paymentMethod && errors.paymentMethod}
                >
                  <MenuItem value="cash">Cash</MenuItem>
                  <MenuItem value="online">Online</MenuItem>
                  <MenuItem value="cheque">Cheque</MenuItem>
                  <MenuItem value="bank_transfer">Bank Transfer</MenuItem>
                  <MenuItem value="card">Card</MenuItem>
                  <MenuItem value="other">Other</MenuItem>
                </TextField>

                {values.paymentType === "partial" ? (
                  <TextField
                    fullWidth
                    margin="normal"
                    label="Paid Amount"
                    name="paidAmount"
                    type="number"
                    value={values.paidAmount}
                    onChange={handleChange}
                    error={touched.paidAmount && !!errors.paidAmount}
                    helperText={touched.paidAmount && errors.paidAmount}
                  />
                ) : (
                  <TextField
                    fullWidth
                    margin="normal"
                    label="Paid Amount"
                    value={values.amount || 0}
                    disabled
                    helperText="For Full payment, paid amount equals amount"
                  />
                )}
              </Stack>

              <TextField
                fullWidth
                margin="normal"
                label="Description (optional)"
                name="description"
                value={values.description}
                onChange={handleChange}
                multiline
                rows={2}
              />

              <Button
                type="submit"
                variant="contained"
                fullWidth
                disabled={isSubmitting}
                sx={{ mt: 2, borderRadius: 2, fontWeight: 900, py: 1.1 }}
              >
                {isEdit ? "Update" : "Save"}
              </Button>
            </Box>
          )}
        </Formik>
      </DialogContent>
    </Dialog>
  );
}
